'use client';

import { useState, useRef } from 'react';
import { useRouter } from '@/i18n/routing';
import { createPerson, updatePerson } from "@/lib/actions/people";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Save, Upload, X, Plus, Trash2 } from "lucide-react";
import { Link } from "@/i18n/routing";

import { RichTextEditor } from "../shared/RichTextEditor";
import { MediaImg } from '@/components/shared/MediaImg';
import { parseJsonField } from '@/lib/utils';

interface PersonFormProps {
  initialData?: any;
}

export function PersonForm({ initialData }: PersonFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEditing = !!initialData;

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    slug: initialData?.slug || '',
    position: initialData?.position || '',
    email: initialData?.email || '',
    linkedin_url: initialData?.linkedin_url || '',
    bio: initialData?.bio || '',
    image: initialData?.image || '',
    order: initialData?.order || 0,
    experience: parseJsonField(initialData?.experience, []),
    education_certifications: parseJsonField(initialData?.education_certifications, []),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === 'name' && !isEditing) {
        newData.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      }
      return newData;
    });
  };

  const handleManualChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setIsUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('type', 'people');

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: uploadData,
        credentials: 'include',
      });

      const result = await response.json();

      if (result.success) {
        setFormData(prev => ({ ...prev, image: result.url }));
        toast.success('Image uploaded successfully');
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch (error) {
      toast.error('An error occurred during upload');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: '' }));
  };

  const handleArrayChange = (field: string, index: number, value: string) => {
    setFormData((prev: any) => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const addArrayItem = (field: string) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field: string, index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: prev[field].filter((_: any, i: number) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = isEditing 
        ? await updatePerson(initialData.id, formData)
        : await createPerson(formData);

      if (result.success) {
        toast.success(`Team member ${isEditing ? 'updated' : 'created'} successfully`);
        router.push('/admin/people');
      } else {
        toast.error(result.error || "Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to save team member");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-20">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link href="/admin/people">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold text-navy">
          {isEditing ? 'Edit Team Member' : 'Add New Team Member'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug (URL)</Label>
                  <Input id="slug" name="slug" value={formData.slug} onChange={handleChange} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="position">Position</Label>
                  <Input id="position" name="position" value={formData.position} onChange={handleChange} required />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="e.g. john@simisi.id" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linkedin_url">LinkedIn URL</Label>
                    <Input id="linkedin_url" name="linkedin_url" value={formData.linkedin_url} onChange={handleChange} placeholder="https://linkedin.com/in/..." />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bio">Biography</Label>
                  <RichTextEditor 
                    value={formData.bio} 
                    onChange={(val) => handleManualChange('bio', val)} 
                  />
                </div>
              </CardContent>
            </Card>

            {/* Experience Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between py-4">
                <CardTitle className="text-lg">Professional Experience</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('experience')}>
                  <Plus className="h-4 w-4 mr-2" /> Add Experience
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {formData.experience.map((item: string, index: number) => (
                  <div key={index} className="flex gap-2">
                    <Input 
                      value={item} 
                      onChange={(e) => handleArrayChange('experience', index, e.target.value)} 
                      placeholder="e.g. Head of Academy at GoTo Group"
                    />
                    <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={() => removeArrayItem('experience', index)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                {formData.experience.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4 border border-dashed rounded-lg">No experience items added.</p>
                )}
              </CardContent>
            </Card>

            {/* Education Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between py-4">
                <CardTitle className="text-lg">Education & Certifications</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('education_certifications')}>
                  <Plus className="h-4 w-4 mr-2" /> Add Item
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {formData.education_certifications.map((item: string, index: number) => (
                  <div key={index} className="flex gap-2">
                    <Input 
                      value={item} 
                      onChange={(e) => handleArrayChange('education_certifications', index, e.target.value)} 
                      placeholder="e.g. Master of Business Administration - UGM"
                    />
                    <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={() => removeArrayItem('education_certifications', index)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                {formData.education_certifications.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4 border border-dashed rounded-lg">No education items added.</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar Settings */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Profile Photo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  {formData.image ? (
                    <div className="relative aspect-square rounded-lg border overflow-hidden group">
                      <MediaImg src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-square rounded-lg border-2 border-dashed border-light flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-light transition-all"
                    >
                      <Upload className="h-8 w-8 text-gray mb-2" />
                      <span className="text-xs text-gray font-medium">Click to upload photo</span>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    accept="image/*"
                  />
                  
                  {isUploading && (
                    <p className="text-xs text-center text-gray-500 animate-pulse">Uploading...</p>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="image">Image Path</Label>
                    <Input
                      id="image"
                      name="image"
                      value={formData.image}
                      onChange={handleChange}
                      placeholder="/upload/people/..."
                      className="text-xs"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="order">Display Order</Label>
                  <Input id="order" name="order" type="number" value={formData.order} onChange={handleChange} />
                  <p className="text-xs text-muted-foreground">Lower numbers appear first on the website.</p>
                </div>
              </CardContent>
            </Card>

            <Button
              type="submit"
              className="w-full bg-navy hover:bg-primary"
              disabled={isLoading || isUploading}
            >
              {isLoading ? 'Saving...' : <><Save className="mr-2 h-4 w-4" /> Save Member</>}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
